/**
 * screenshotScanPipeline — the screenshot reader's classify + save core,
 * extracted from ScanPokedexModal (2026-08-27) so the background Auto Scan
 * runner (autoScanRunner.ts) and the modal share ONE authority. The lesson
 * behind the extraction is dexStateStore's two-apply-paths scar: a second,
 * slightly different copy of scan logic is exactly how silent divergence
 * starts, so the modal now delegates here rather than the runner cloning it.
 *
 * classifyScreenshot(): one photo → a Pending row (grid / detail /
 * my-pokemon) or a "skip" verdict for non-GO photos. Includes the trainer
 * wallet side-effect (stardust/candy recording) the my-pokemon probe does.
 *
 * saveScreenshotRows(): writes a batch of rows through the SAME store paths
 * as before (dexStateStore observations, applyDetailFrame, My Pokémon
 * upserts, legacy aggregate POST), inside one scan-history session so one
 * batch is one undo entry. Returns honest counts for the caller's toasts.
 */
import { apiRequest } from "@/lib/queryClient";
import { sha256OfString } from "@/lib/screenshotBatch";
import type { PokedexGridFrame, PokedexFilter } from "@/lib/pokedexOcr";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import type { PokemonDetailFrame } from "@/lib/pokemonDetailDexOcr";
import type { MyPokemonFrame } from "@/lib/myPokemonOcr";
import { mergeIntoRecordById, upsertScannedPokemon } from "@/lib/myPokemonStore";
import { detailScanFeedback } from "@/lib/pokemonDetailDexOcr";
import { setDexPipPhase } from "@/lib/nexPip";
import { reportScanPerf } from "@/lib/ocrEngine";
import { getPlatform } from "@/lib/platform";
import {
  resolveScreenshotScanVariant,
  type ScreenshotVariantDecision,
} from "@/lib/screenshotScanVariant";
import { applyScanObservations, commitDexTotals } from "@/lib/dexStateStore";
import {
  beginScanSession,
  endScanSession,
  recordMyPokemonScanRow,
} from "@/lib/scanSessionStore";
import {
  publishDexScanDiagnostics,
  type DexScanDiagnostics,
} from "@/lib/dexScanDiagnostics";
import {
  topPanelBulkObservations,
  topPanelDiagnostics,
} from "@/lib/topPanelOcr";

/**
 * A parsed Pokédex GRID screenshot (one variant, many species) — the original
 * scan path. Tagged with `kind: "grid"` so the review + save code can branch.
 */
export interface GridPending {
  kind: "grid";
  fileName: string;
  frame: PokedexGridFrame;
  contentHash: string;
  /**
   * The Dex variant this frame will write to. `null` when the active-pill read
   * was inconclusive AND the user has not yet confirmed a variant — such rows
   * block saving so we never write to the wrong (or a leaked) Dex.
   */
  variant: PokedexVariant | null;
  /** How `variant` was resolved (active-pill text / colour / unresolved). */
  variantSource: ScreenshotVariantDecision["source"];
  /**
   * True when the classifier could not pin a variant from the screenshot and the
   * user must explicitly confirm one before this row can be saved.
   */
  needsConfirmation: boolean;
  /** The viewed-tab suggestion used to pre-fill the confirmation picker. */
  suggested: PokedexVariant | null;
}

/**
 * A parsed Pokémon DETAIL page (one species, many variants) — the Pro feature.
 * `locked` is true when the uploader lacks Pro; the row renders an upsell
 * instead of saving.
 */
export interface DetailPending {
  kind: "detail";
  fileName: string;
  frame: PokemonDetailFrame;
  contentHash: string;
  locked: boolean;
}

/**
 * A parsed INDIVIDUAL caught-Pokémon page — feeds the My Pokémon database,
 * not the dex. `unresolved` marks a nicknamed Pokémon whose species could not
 * be named from this one screenshot — shown, never silently dropped.
 */
export interface MyPokemonPending {
  kind: "myPokemon";
  fileName: string;
  frame: MyPokemonFrame;
  contentHash: string;
  unresolved: boolean;
  /**
   * A shot with none of the identity anchors (no CP, no max HP, no IV bars):
   * the scrolled middle or bottom of a page (2026-09-03 owner report: three
   * shots of one Mewtwo made three records). Saved into the batch's previous
   * Pokémon rather than as a record of its own.
   */
  continuation?: boolean;
}

export type Pending = GridPending | DetailPending | MyPokemonPending;

export type ClassifiedScreenshot =
  | { kind: "pending"; pending: Pending }
  | { kind: "skip" };

/** A row Auto Scan must NOT save on its own — it needs a human decision. */
export function needsHumanReview(p: Pending): boolean {
  return (
    (p.kind === "grid" && p.variant == null) ||
    (p.kind === "detail" && p.locked) ||
    (p.kind === "myPokemon" && p.unresolved)
  );
}

export function variantToFilter(v: PokedexVariant): PokedexFilter {
  switch (v) {
    case "shiny": return "shiny";
    case "lucky": return "lucky";
    case "shadow": return "shadow";
    case "purified": return "purified";
    case "perfect": return "three_star";
    case "xxl": return "xxl";
    case "xxs": return "xxs";
    case "mega":
    case "gmax":
    case "pokemon":
    default:
      return "caught";
  }
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Classify one photo. Probe order is load-bearing: detail page first (Pro), a
 * trainer's own caught-Pokémon page second (its stats must NEVER reach the
 * grid reader, they'd parse as dex numbers), the Pokédex grid last with the
 * looksLikePokedex gate that lets Auto Scan chew a whole camera roll without
 * producing junk rows for non-GO photos.
 */
export async function classifyScreenshot(
  file: File,
  opts: { initialVariant?: PokedexVariant; isPro: boolean },
): Promise<ClassifiedScreenshot> {
  const dataUrl = await readFileAsDataUrl(file);
  const { extractPokedexFrame } = await import("@/lib/pokedexOcr");
  const { extractPokemonDetailPage } = await import("@/lib/pokemonDetailDexOcr");

  const detail = await extractPokemonDetailPage(dataUrl);
  if (detail.isDetailPage && detail.dexNumber != null) {
    const contentHash = await sha256OfString(
      `detail|${file.name}|${file.size}|${detail.dexNumber}|${detail.formId ?? ""}`,
    );
    return {
      kind: "pending",
      pending: {
        kind: "detail",
        fileName: file.name,
        frame: detail,
        contentHash,
        locked: !opts.isPro,
      },
    };
  }

  const { extractMyPokemonPage } = await import("@/lib/myPokemonOcr");
  const mp = await extractMyPokemonPage(dataUrl);
  const observedAt = file.lastModified > 0 ? file.lastModified : undefined;
  if (mp.isMegaLevelPage && mp.megaLevelPage) {
    // GO's Mega Level page (2026-09-03): applied on classification like the
    // wallet is, since there is no record to hold for review. Reported as
    // skipped in the batch count because it creates no row.
    const { applyMegaFrame } = await import("@/lib/megaScanApply");
    applyMegaFrame(mp, observedAt ?? Date.now());
    return { kind: "skip" };
  }
  if (mp.isMyPokemonPage) {
    if (mp.speciesId != null && ((mp.megaReads?.length ?? 0) > 0 || mp.megaRestMs != null)) {
      const { applyMegaFrame } = await import("@/lib/megaScanApply");
      applyMegaFrame(mp, observedAt ?? Date.now());
    }
    // Trainer wallet pipe: the photo path records the page's stardust +
    // family candy the same way the live scan does, so an uploaded
    // screenshot also keeps the Stardust Planner's balance fresh.
    if (mp.stardust != null || mp.candyFamilyName != null || mp.megaEnergy != null) {
      const [{ recordTrainerResources }, { speciesIdFromDisplayName }] =
        await Promise.all([
          import("@/lib/trainerResources"),
          import("@/lib/myPokemonOcr"),
        ]);
      recordTrainerResources({
        stardust: mp.stardust,
        candyFamilySpeciesId: mp.candyFamilyName
          ? speciesIdFromDisplayName(mp.candyFamilyName)
          : null,
        candy: mp.candy,
        candyXl: mp.candyXl,
        megaEnergySpeciesId: mp.megaEnergySpeciesName
          ? speciesIdFromDisplayName(mp.megaEnergySpeciesName)
          : null,
        megaEnergy: mp.megaEnergy,
        megaEnergies: (mp.megaEnergies ?? []).flatMap((e) => {
          const speciesId = speciesIdFromDisplayName(e.speciesName);
          return speciesId != null ? [{ speciesId, formLabel: e.formLabel, energy: e.energy }] : [];
        }),
        // The photo's time, not the upload's: a screenshot from before the
        // last live scan must not roll the balance back, and one taken after
        // it should win (2026-09-03 owner ask).
        observedAt: file.lastModified > 0 ? file.lastModified : undefined,
        source: "screenshot",
      });
    }
    const contentHash = await sha256OfString(
      `mypokemon|${file.name}|${file.size}|${mp.speciesId ?? mp.displayName ?? ""}|${mp.cp ?? ""}|${mp.hpMax ?? ""}`,
    );
    const continuation = mp.cp == null && mp.hpMax == null && mp.iv == null;
    return {
      kind: "pending",
      pending: {
        kind: "myPokemon",
        fileName: file.name,
        frame: mp,
        contentHash,
        // A continuation shot needs no species of its own: it rides the
        // previous row's. Only a shot that could stand alone is held for
        // a species when it has none.
        unresolved: mp.speciesId == null && !continuation,
        continuation,
      },
    };
  }

  const frame = await extractPokedexFrame(dataUrl);
  const hashSeed = `${file.name}|${file.size}|${frame.activeRegion}|${frame.activeFilter}|${frame.progressTotal}`;
  const contentHash = await sha256OfString(hashSeed);
  // Variant routing: the SAME authority as the live scan (active-pill text >
  // wash colour); when it cannot pin a variant, the row requires explicit
  // confirmation instead of inheriting the viewed tab.
  const decision = resolveScreenshotScanVariant(frame, opts.initialVariant);
  // NOT A POKÉMON GO SCREEN: no probe hit, no readable dex cell, no progress
  // counter AND no variant signal → skipped (and counted by the caller),
  // never pushed as a junk "Region?" row.
  const looksLikePokedex =
    frame.cells.some((c) => c.dexNumber != null) ||
    frame.progressTotal != null ||
    decision.variant != null;
  if (!looksLikePokedex) return { kind: "skip" };
  // Unreadable Dex type → SKIP, don't hold for review (2026-08-28 device
  // report): the review row shows no image, so "Select Dex type..." was a
  // blind guess — exactly the mis-write the confirmation gate exists to
  // prevent. Skipping is honest: the count surfaces it, and rescanning the
  // tab with the selector visible reads cleanly.
  if (decision.variant == null) return { kind: "skip" };
  return {
    kind: "pending",
    pending: {
      kind: "grid",
      fileName: file.name,
      frame,
      contentHash,
      variant: decision.variant,
      variantSource: decision.source,
      needsConfirmation: decision.needsConfirmation,
      suggested: decision.suggested,
    },
  };
}

export interface SaveSummary {
  saved: number;
  rejected: number;
  dexStateApplied: number;
  myPokemonSaved: number;
  detailFeedbacks: Array<{ line1: string; line2: string }>;
}

/**
 * Save a batch of rows. One scan-history session wraps the whole batch (one
 * Save = one undo entry), and the dex totals snapshot commits at the end so
 * grids and rings reflect the write immediately. Callers own the toasts.
 */
export async function saveScreenshotRows(
  rows: readonly Pending[],
  source: "upload" | "auto" = "upload",
): Promise<SaveSummary> {
  let saved = 0;
  let rejected = 0;
  let dexStateApplied = 0;
  let myPokemonSaved = 0;
  const detailFeedbacks: Array<{ line1: string; line2: string }> = [];
  const startedAt = Date.now();
  const { applyDetailFrame } = await import("@/lib/pokemonDetailDexOcr");
  // A screenshot upload is a scan too, so it gets its own session in the scan
  // history: the trainer who uploads the wrong screenshot needs the same undo
  // as the trainer whose live scan misread a cell.
  const uploadScanId = `${source}-${Date.now()}`;
  beginScanSession(uploadScanId, "upload");
  // The batch's previous caught-Pokémon row: a continuation shot (no CP, no
  // HP, no IVs) of the same species, or of no readable species, merges into
  // it instead of minting a record.
  let lastMyPokemon: { id: string; speciesId: number | null } | null = null;
  try {
    for (const p of rows) {
      // Individual caught-Pokémon rows → the My Pokémon database. Free
      // (parity with the live-scan path); an unresolved nickname is counted
      // rejected rather than guessed at.
      if (p.kind === "myPokemon") {
        if (
          p.continuation &&
          lastMyPokemon &&
          (p.frame.speciesId == null || p.frame.speciesId === lastMyPokemon.speciesId)
        ) {
          const merged = mergeIntoRecordById(lastMyPokemon.id, p.frame);
          if (merged) {
            if (merged.changed) myPokemonSaved += 1;
            saved += 1;
            continue;
          }
        }
        if (p.unresolved || (p.continuation && p.frame.speciesId == null)) {
          rejected += 1;
          continue;
        }
        // The path that added it (2026-09-16): the library picker or the
        // camera-roll Auto Scan. Only a CREATE reads it.
        const res = upsertScannedPokemon({
          ...p.frame,
          addedVia: source === "auto" ? "auto_scan" : "screenshot",
        });
        lastMyPokemon = { id: res.record.id, speciesId: res.record.speciesId };
        if (res.created || res.changed) myPokemonSaved += 1;
        // Pokémon this batch CREATED land in the scan history alongside dex
        // writes, distinguished by target (and undoable: undo deletes the
        // record). Merges into existing records are not logged — deleting a
        // pre-scan record is not an undo of this scan.
        if (res.created && res.record.speciesId != null) {
          recordMyPokemonScanRow({
            myPokemonId: res.record.id,
            speciesId: res.record.speciesId,
            formId: res.record.formId,
          });
        }
        saved += 1;
        continue;
      }
      // Detail-page rows (Pro): one screenshot updates every dex — and every
      // form — for one species, through the same dexStateStore path as the
      // grid scan (manual overrides win, MAIN-dex cascade fires).
      if (p.kind === "detail") {
        if (p.locked) {
          rejected += 1;
          continue;
        }
        const res = applyDetailFrame(p.frame);
        dexStateApplied += res.applied;
        saved += 1;
        const fb = detailScanFeedback(p.frame, res.applied);
        detailFeedbacks.push(fb);
        void setDexPipPhase({
          phase: "saved",
          message: fb.line1,
          detail: fb.line2,
          appliedCount: res.applied,
        });
        continue;
      }

      const { frame, variant } = p;

      // Safety gate: never write a frame whose variant was not confidently
      // read and not yet confirmed by the user.
      if (variant == null) {
        rejected += 1;
        continue;
      }

      // 1. Pure Dex-state ingestion via dexStateStore. ONLY the cells
      //    actually observed become observations — never fabricate missing
      //    entries. When the trusted top panel reports the MAIN Dex fully
      //    caught for the region, bulk-mark it non-destructively (main
      //    variant only; the panel count is always the MAIN count).
      const topPanel = frame.topPanel;
      const bulkObservations =
        variant === "pokemon" && topPanel?.action === "bulk_region_complete"
          ? topPanelBulkObservations(topPanel)
          : [];

      const observations = frame.cells
        .filter((c) => c.dexNumber != null)
        .map((c) => {
          // Pokédex grid in legacy OCR starts at y 0.17 with cellH 0.155;
          // yPct feeds the store's overlay-fence helper.
          const yPct = 0.17 + (c.rowIndex + 0.5) * 0.155;
          return { dexNumber: c.dexNumber as number, state: "present" as const, yPct };
        });
      const result = applyScanObservations(variant, [...bulkObservations, ...observations]);
      dexStateApplied += result.applied;

      // Diagnostic snapshot for the Dex Live Scan menu's panel.
      const numbersRead = observations.length;
      const diag: DexScanDiagnostics = {
        ts: Date.now(),
        source: "upload",
        variant,
        variantInferred: p.needsConfirmation,
        region: frame.activeRegion,
        mode: "grid_4x4",
        confidence: frame.confidence,
        cellsObserved: frame.cells.length,
        numbersRead,
        applied: result.applied,
        skippedManual: result.skippedManual,
        unknownCells: Math.max(0, frame.cells.length - numbersRead),
        note: frame.warnings[0],
        ...(topPanel ? { topPanel: topPanelDiagnostics(topPanel) } : {}),
      };
      publishDexScanDiagnostics(diag);

      // 2. Legacy aggregate post (kept so the existing /api/dex/ completion
      //    read model still updates for users who already rely on it).
      if (frame.progressTotal == null || frame.progressSeen == null) {
        rejected += 1;
        continue;
      }
      try {
        await apiRequest("POST", "/api/dex/pokedex-scan", {
          contentHash: p.contentHash,
          region: frame.activeRegion,
          filter: variantToFilter(variant),
          completed: frame.progressSeen,
          total: frame.progressTotal,
          sourceScreenshotHash: p.contentHash,
        });
        saved += 1;
      } catch (err) {
        console.warn("[screenshotScanPipeline] save failed", err);
        rejected += 1;
      }
    }
  } finally {
    endScanSession(uploadScanId);
  }
  // Owning a Pokémon proves the dex tick, on THIS save path too: screenshot
  // and quick-add batches used to wait for the next My Pokémon page mount to
  // cross-write, so the dex lagged the scan. Additive, roster-gated, and
  // governed by the Settings "Update Pokédex from scans" switch (the gate
  // lives inside crossWriteMyPokemonToDex).
  if (myPokemonSaved > 0) {
    try {
      const { crossWriteMyPokemonToDex } = await import("@/lib/myPokemonDexCrossWrite");
      const { listMyPokemon } = await import("@/lib/myPokemonStore");
      crossWriteMyPokemonToDex(listMyPokemon());
    } catch {
      // Dex bookkeeping must never fail the save the trainer just made.
    }
  }
  // Dex-reload trigger: commit the totals snapshot at the source of the write
  // so cells light up and completion rings update the moment the batch lands.
  commitDexTotals();
  // One scan_perf row per batch (2026-09-16): until now only the live HUD
  // reported, so the admin journey never saw an upload or an Auto Scan run.
  // `source` names the path, `imageSize` is the batch size, success is
  // "anything landed", same rule as the live run report.
  if (rows.length > 0) {
    const platform = getPlatform();
    // A batch that saved nothing can only say "unknown" (2026-10-15): the
    // classifier already dropped non-GO photos before these rows existed, and
    // the rows that remain were rejected for a confirmation the trainer did
    // not give (a variant, Pro, a nickname), which is not a scanner outcome.
    void reportScanPerf({
      source: source === "auto" ? "auto_scan" : "screenshot",
      engine: platform === "ios" ? "apple-vision" : platform === "android" ? "ml-kit" : "web",
      imageSize: rows.length,
      ocrMs: 0,
      totalMs: Math.max(0, Date.now() - startedAt),
      success: saved > 0,
      outcome: saved > 0 ? undefined : "unknown",
    });
  }
  return { saved, rejected, dexStateApplied, myPokemonSaved, detailFeedbacks };
}
